import React, { useEffect, useState, memo } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CardMedia,
  Chip,
  Skeleton,
  IconButton,
  Tooltip,
  Snackbar,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { useNavigate } from "react-router-dom";
import { renderDoc } from "../../requests/s3";
import { setLoading } from "../../store/uiSlice";
import { useDispatch } from "react-redux";
import sanitizeHtml from "sanitize-html";
import useFavorites from "../../hooks/useFavorites";
import useCompare from "../../hooks/useCompare";
import {
  FacebookShareButton,
  FacebookIcon,
  WhatsappShareButton,
  WhatsappIcon,
  EmailShareButton,
  EmailIcon,
  TwitterShareButton,
  LinkedinShareButton,
  LinkedinIcon,
} from "react-share";
import TwitterIcon from "react-share/lib/TwitterIcon";

type Props = {
  annonce: any;
  id?: string;
};

const owners = ["65118bef4a8a916f7a2e1970", "650d7fa54a8a916f7a2e1096"];

const AnnonceCard = memo(({ annonce, id }: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [image, setImage] = useState("");
  const [imageLoading, setImageLoading] = useState(true);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const { toggleFavorite, isFavorite } = useFavorites();
  const { toggleCompare, isInCompare, canAddMore } = useCompare();

  // Gestion du clic sur le bouton favori
  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(annonce._id);
  };

  // Gestion du clic sur le bouton comparer
  const handleCompareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const result = toggleCompare(annonce._id);
    if (result.error) {
      setSnackbarMessage(result.error);
      setCopySnackbar(true);
    } else if (result.added) {
      setSnackbarMessage("Ajouté à la comparaison");
      setCopySnackbar(true);
    }
  };

  // URL de partage
  const shareUrl = `https://www.direct-proprietaire.fr/annonce/${annonce._id}`;
  const shareTitle = annonce.data.titre || "Annonce immobilière";

  // Copie le lien dans le presse-papier
  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(shareUrl);
    setSnackbarMessage("Lien copié !");
    setCopySnackbar(true);
  };
  let content = sanitizeHtml(annonce.data.data.informations.description, {
    allowedTags: [],
  });
  // let content = annonce.data.data.informations.description.replace("\n", "");
  // content = content.replaceAll("<p>", "");
  // content = content.replaceAll('<p style="text-align: justify;">', "");
  // content = content.replaceAll("</p>", " ");
  // content = content.replaceAll("<ul>", "");
  // content = content.replaceAll("</ul>", " ");
  // content = content.replaceAll("<li>", "");
  // content = content.replaceAll("</li>", " ");
  // content = content.replaceAll("<div>", "");
  // content = content.replaceAll("</div>", " ");
  // content = content.replaceAll("<span>", "");
  // content = content.replaceAll("</span>", " ");
  // content = content.replaceAll("<ol>", "");
  // content = content.replaceAll("</ol>", " ");
  // content = content.replaceAll("<br />", " ");

  useEffect(() => {
    setImageLoading(true);
    dispatch(setLoading({ isLoading: true }));
    if (annonce !== undefined && annonce.data.thumbnail?.imageUrl) {
      renderDoc(annonce.data.thumbnail.imageUrl).then((res) => {
        if (res !== "error") {
          setImage(res.url);
          dispatch(setLoading({ isLoading: false }));
        } else {
          setImageLoading(false);
          dispatch(setLoading({ isLoading: false }));
        }
      });
    } else {
      setImageLoading(false);
      dispatch(setLoading({ isLoading: false }));
    }
  }, [annonce]);

  return annonce ? (
    <Grid item xs={1}>
      <Card
        component="article"
        sx={{
          boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.15)",
          borderRadius: "20px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          "&:hover": {
            cursor: "pointer",
          },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: "2px",
          },
        }}
        tabIndex={0}
        role="button"
        aria-label={`Voir l'annonce : ${annonce.data.titre || "Annonce immobilière"} à ${annonce.data.data.informations.localisation.data} - ${new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(annonce.data.data.informations.prix.data)}`}
        onClick={() => navigate(`/annonce/${annonce._id}`)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate(`/annonce/${annonce._id}`);
          }
        }}
      >
        {/* Skeleton loader pendant le chargement de l'image */}
        {imageLoading && (
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{ height: 350 }}
          />
        )}
        <CardMedia
          component="img"
          sx={{
            height: 350,
            objectFit: "cover",
            display: imageLoading ? "none" : "block"
          }}
          image={image}
          alt={annonce.data.titre || "Photo du bien"}
          onLoad={() => setImageLoading(false)}
          onError={() => setImageLoading(false)}
        />
        {/* Boutons favori et comparer */}
        <Box
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            display: "flex",
            gap: 1,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title={isFavorite(annonce._id) ? "Retirer des favoris" : "Ajouter aux favoris"}>
            <IconButton
              onClick={handleFavoriteClick}
              sx={{
                backgroundColor: "white",
                "&:hover": { backgroundColor: "grey.100" },
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
              aria-label={isFavorite(annonce._id) ? "Retirer des favoris" : "Ajouter aux favoris"}
            >
              {isFavorite(annonce._id) ? (
                <FavoriteIcon sx={{ color: "#e91e63" }} />
              ) : (
                <FavoriteBorderIcon sx={{ color: "grey.600" }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              isInCompare(annonce._id)
                ? "Retirer de la comparaison"
                : canAddMore
                ? "Ajouter à la comparaison"
                : "Maximum 3 biens à comparer"
            }
          >
            <IconButton
              onClick={handleCompareClick}
              sx={{
                backgroundColor: isInCompare(annonce._id) ? "primary.main" : "white",
                "&:hover": {
                  backgroundColor: isInCompare(annonce._id) ? "primary.dark" : "grey.100",
                },
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
              aria-label={isInCompare(annonce._id) ? "Retirer de la comparaison" : "Ajouter à la comparaison"}
            >
              <CompareArrowsIcon
                sx={{
                  color: isInCompare(annonce._id) ? "white" : "grey.600",
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
        {owners.includes(annonce.owner) ? (
          <Chip
            label={"Annonce test"}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              backgroundColor: "white",
            }}
          />
        ) : null}
        <CardContent
          sx={{
            // flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          <Box display="flex" justifyContent={"space-between"} pb={2}>
            <Typography sx={{ color: "#FFA400" }}>
              {annonce.data.data.informations.localisation.data}
            </Typography>
            <Typography sx={{ color: "#FFA400" }}>
              {new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "EUR",
                maximumFractionDigits: 0,
              }).format(annonce.data.data.informations.prix.data)}
            </Typography>
          </Box>
          <Typography pb={2}>{annonce.data.titre}</Typography>
          <Box pb={2}>
            {annonce.data.data.informations.nombrePieces?.data || "?"} pièces /{" "}
            {annonce.data.data.informations.nombreChambres?.data || "?"} chambres /{" "}
            {annonce.data.data.informations.surface?.data || "?"}m²
          </Box>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "4",
              WebkitBoxOrient: "vertical",
            }}
          >
            {content}
          </Typography>

          {/* Boutons de partage */}
          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            gap={0.5}
            mt={2}
            pt={2}
            borderTop="1px solid"
            borderColor="grey.200"
            onClick={(e) => e.stopPropagation()}
            sx={{
              // Normalise les boutons react-share pour retirer l'espacement en bas
              "& button": {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                background: "none",
                border: "none",
                cursor: "pointer",
                lineHeight: 0,
              },
            }}
          >
            <Box display="flex" alignItems="center" mr={1}>
              <ShareIcon sx={{ fontSize: 14, mr: 0.5, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary">
                Partager
              </Typography>
            </Box>
            <FacebookShareButton url={shareUrl} title={shareTitle}>
              <FacebookIcon size={28} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={shareTitle}>
              <TwitterIcon size={28} round />
            </TwitterShareButton>
            <LinkedinShareButton url={shareUrl} title={shareTitle}>
              <LinkedinIcon size={28} round />
            </LinkedinShareButton>
            <WhatsappShareButton url={shareUrl} title={shareTitle}>
              <WhatsappIcon size={28} round />
            </WhatsappShareButton>
            <EmailShareButton
              url={shareUrl}
              subject={shareTitle}
              body={`Découvrez cette annonce immobilière : ${shareTitle}`}
            >
              <EmailIcon size={28} round />
            </EmailShareButton>
            <Tooltip title="Copier le lien">
              <IconButton
                onClick={handleCopyLink}
                sx={{
                  backgroundColor: "grey.200",
                  width: 28,
                  height: 28,
                  p: 0,
                  "&:hover": { backgroundColor: "grey.300" },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* Notification */}
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message={snackbarMessage || "Lien copié !"}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Grid>
  ) : null;
});

// Display name pour le debugging React DevTools
AnnonceCard.displayName = "AnnonceCard";

export default AnnonceCard;
